import { StyledSelect } from "@ando/ui";
/**
 * EmailSyncFiltersSheet — V2 port of V1 ``EmailSyncFilters.tsx`` (May 25, 2026).
 *
 * Mirrors V1's UX 1:1 (sections / copy / validation rules) but rebuilt
 * on V2's design system: Radix Drawer (side="right") + Tailwind cream/ink
 * palette + ``@ando/ui`` primitives.
 *
 * Section order (top → bottom — V1 parity):
 *   1. Header (vendor chip + email + active-sync banner)
 *   2. Saved presets (chip row, click to load)
 *   3. Time range (7d / 30d / 90d / Custom + date inputs)
 *   4. Labels (system labels first row + collapsible user labels)
 *   5. Quick filters (has_attachment, is_unread)
 *   6. Advanced (collapsible — Gmail query + max_messages dropdown)
 *   7. Preview strip (count + ETA, debounced 500ms)
 *   8. Actions (Sync + Save preset)
 *
 * Validation rules (V1 parity, copied verbatim):
 *   - Sync button disabled when: syncing | needs_reauth | no preview |
 *     preview count = 0 | customRangeError !== null
 *   - Custom range: before > after, max 90 days
 */

import { Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader, cn, toast } from "@ando/ui";
import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ChevronDown,
  Filter,
  Loader2,
  Mail,
  Paperclip,
  RefreshCw,
  Save,
  Search,
  X,
} from "lucide-react";
import * as React from "react";

import {
  type EmailLabel,
  type EmailProvider,
  type EmailSyncFilters,
  useDeleteEmailPreset,
  useEmailForceSync,
  useEmailLabels,
  useEmailPresets,
  useEmailPreviewSync,
  useEmailSyncStatus,
  useEmailSyncTrigger,
  useSaveEmailPreset,
} from "@/features/sync/hooks/useEmailSync";

// ──────────────────────────────────────────────────────────────────────
// Constants (V1 parity)
// ──────────────────────────────────────────────────────────────────────

const MAX_CUSTOM_RANGE_DAYS = 90;
const PREVIEW_DEBOUNCE_MS = 500;
/**
 * Max-messages dropdown values (V1 parity). The 100k ceiling matches
 * the BE's ``EMAIL_MAX_MESSAGES`` env default; bigger jobs need an
 * explicit operator override.
 */
const MAX_MESSAGES_OPTIONS = [500, 2_000, 10_000, 100_000] as const;

const DEFAULT_FILTERS: EmailSyncFilters = {
  time_range: "30d",
  labels: [],
  has_attachment: false,
  is_unread: false,
};

// ──────────────────────────────────────────────────────────────────────
// Helpers — debounced effect (inlined; no dep on use-debounce)
// ──────────────────────────────────────────────────────────────────────

function useDebouncedEffect(
  effect: React.EffectCallback,
  deps: React.DependencyList,
  delayMs: number,
): void {
  React.useEffect(() => {
    const id = window.setTimeout(() => {
      effect();
    }, delayMs);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

function diffDays(a: string, b: string): number {
  const da = new Date(a);
  const db = new Date(b);
  return Math.floor((db.getTime() - da.getTime()) / 86_400_000);
}

function validateCustomRange(after: string, before: string, i18n: I18n): string | null {
  if (!after || !before) return null;
  if (new Date(before) <= new Date(after)) {
    return i18n._(msg`Intervallo date non valido (fine deve essere dopo l'inizio).`);
  }
  if (diffDays(after, before) > MAX_CUSTOM_RANGE_DAYS) {
    return i18n._(
      msg`L'intervallo personalizzato non può superare ${MAX_CUSTOM_RANGE_DAYS} giorni.`,
    );
  }
  return null;
}

function formatEta(seconds: number | undefined, i18n: I18n): string | null {
  if (!seconds || seconds <= 0) return null;
  const m = Math.ceil(seconds / 60);
  return m <= 1 ? i18n._(msg`~1 min`) : i18n._(msg`~${m} min`);
}

// ──────────────────────────────────────────────────────────────────────
// Public API
// ──────────────────────────────────────────────────────────────────────

export interface EmailSyncFiltersSheetProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly emailAddress: string;
  readonly provider: EmailProvider;
  /** Optional initial filter set — e.g. last-used preset. */
  readonly initialFilters?: EmailSyncFilters;
  /** Fires after the sync mutation succeeds (parent can close + toast). */
  readonly onSyncStarted?: (jobId: string | null) => void;
}

export const EmailSyncFiltersSheet: React.FC<EmailSyncFiltersSheetProps> = ({
  open,
  onOpenChange,
  emailAddress,
  provider,
  initialFilters,
  onSyncStarted,
}) => {
  const { i18n, t } = useLingui();
  // ── Local state ───────────────────────────────────────────────────
  const [filters, setFilters] = React.useState<EmailSyncFilters>(initialFilters ?? DEFAULT_FILTERS);
  const [customAfter, setCustomAfter] = React.useState<string>("");
  const [customBefore, setCustomBefore] = React.useState<string>("");
  const [labelsExpanded, setLabelsExpanded] = React.useState(false);
  const [advancedExpanded, setAdvancedExpanded] = React.useState(false);
  const [maxMessages, setMaxMessages] = React.useState<number>(2_000);
  const [savePresetOpen, setSavePresetOpen] = React.useState(false);
  const [savePresetName, setSavePresetName] = React.useState("");

  // ── Server queries ────────────────────────────────────────────────
  const labels = useEmailLabels(emailAddress, provider);
  const presets = useEmailPresets(emailAddress);
  const syncStatus = useEmailSyncStatus(emailAddress, provider);

  // ── Mutations ─────────────────────────────────────────────────────
  const previewMut = useEmailPreviewSync();
  const triggerMut = useEmailSyncTrigger();
  const forceMut = useEmailForceSync();
  const savePresetMut = useSaveEmailPreset();
  const deletePresetMut = useDeleteEmailPreset(emailAddress);

  // ── Validation ────────────────────────────────────────────────────
  const customRangeError =
    filters.time_range === "custom" ? validateCustomRange(customAfter, customBefore, i18n) : null;

  // ── Debounced preview ─────────────────────────────────────────────
  useDebouncedEffect(
    () => {
      if (filters.time_range === "custom" && (!customAfter || !customBefore)) {
        return; // skip until both dates filled
      }
      if (customRangeError) return;
      const liveFilters: EmailSyncFilters = {
        ...filters,
        after_date: filters.time_range === "custom" ? customAfter : undefined,
        before_date: filters.time_range === "custom" ? customBefore : undefined,
      };
      previewMut.mutate({ emailAddress, provider, filters: liveFilters });
    },
    [filters, customAfter, customBefore, customRangeError],
    PREVIEW_DEBOUNCE_MS,
  );

  const previewData = previewMut.data;
  const previewing = previewMut.isPending;
  const matchingCount = previewData?.matching_emails ?? null;

  // ── Sync trigger ──────────────────────────────────────────────────
  const liveFiltersForSync: EmailSyncFilters = React.useMemo(
    () => ({
      ...filters,
      after_date: filters.time_range === "custom" ? customAfter : undefined,
      before_date: filters.time_range === "custom" ? customBefore : undefined,
    }),
    [filters, customAfter, customBefore],
  );

  const syncDisabled =
    triggerMut.isPending ||
    previewing ||
    customRangeError !== null ||
    matchingCount === 0 ||
    matchingCount === null;

  const handleSync = async () => {
    try {
      const res = await triggerMut.mutateAsync({
        emailAddress,
        provider,
        filters: liveFiltersForSync,
        maxMessages,
      });
      toast.success(t`Sincronizzazione avviata`);
      onSyncStarted?.(res.job_id ?? null);
      onOpenChange(false);
    } catch (err) {
      const reason = err instanceof Error ? err.message : t`errore sconosciuto`;
      toast.error(t`Impossibile avviare la sincronizzazione: ${reason}`);
    }
  };

  // ── Resume / force re-sync ────────────────────────────────────────
  // V1 surfaces two extra paths next to the filtered sync:
  //   - **Resume** — when ``resumable_job_*`` fields are set, the user
  //     can pick up where the previous worker run left off without
  //     re-evaluating filters.
  //   - **Force full re-sync** — bypass filters entirely and re-index
  //     EVERY mail from scratch. Both ride the ``start-sync`` endpoint.
  const resumableJobId = syncStatus?.resumable_job_id;
  const resumableProcessed = syncStatus?.resumable_messages_processed ?? 0;

  const handleResume = async () => {
    try {
      await forceMut.mutateAsync({
        emailAddress,
        provider,
        syncType: "auto",
        forceNew: false,
      });
      toast.success(t`Ripresa della sincronizzazione avviata`);
      onOpenChange(false);
    } catch (err) {
      const reason = err instanceof Error ? err.message : t`errore`;
      toast.error(t`Impossibile riprendere la sincronizzazione: ${reason}`);
    }
  };

  const handleForceFull = async () => {
    try {
      const res = await forceMut.mutateAsync({
        emailAddress,
        provider,
        syncType: "full",
        forceNew: true,
      });
      toast.success(t`Ri-sincronizzazione completa avviata`);
      onSyncStarted?.(res.job_id ?? null);
      onOpenChange(false);
    } catch (err) {
      const reason = err instanceof Error ? err.message : t`errore`;
      toast.error(t`Impossibile avviare la ri-sincronizzazione: ${reason}`);
    }
  };

  // ── Preset save ───────────────────────────────────────────────────
  const handleSavePreset = async () => {
    const name = savePresetName.trim();
    if (name.length === 0) return;
    try {
      await savePresetMut.mutateAsync({
        emailAddress,
        presetName: name,
        filters: liveFiltersForSync,
      });
      toast.success(t`Preset "${name}" salvato`);
      setSavePresetName("");
      setSavePresetOpen(false);
    } catch (err) {
      const reason = err instanceof Error ? err.message : t`errore`;
      toast.error(t`Impossibile salvare il preset: ${reason}`);
    }
  };

  // ── Filter mutations ──────────────────────────────────────────────
  const toggleLabel = (id: string): void => {
    const current = new Set(filters.labels ?? []);
    if (current.has(id)) current.delete(id);
    else current.add(id);
    setFilters({ ...filters, labels: Array.from(current) });
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        side="right"
        ariaTitle={t`Filtri di sincronizzazione email`}
        className="flex w-[min(420px,90vw)] flex-col sm:max-w-none"
      >
        {/* Header */}
        <DrawerHeader>
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-md bg-ando-orange-soft text-ando-orange">
              <Mail className="h-4 w-4" strokeWidth={2} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="truncate font-display font-semibold text-[15px] text-ink-1">
                <Trans>Sincronizza email</Trans>
              </h2>
              <p className="truncate text-[11.5px] text-ink-3">
                {emailAddress} ·{" "}
                {provider === "gmail"
                  ? "Gmail"
                  : provider === "aruba"
                    ? "Aruba PEC"
                    : provider === "infocert"
                      ? "InfoCert PEC"
                      : "Outlook"}
              </p>
            </div>
          </div>
        </DrawerHeader>

        {/* Body */}
        <DrawerBody className="flex-1 overflow-y-auto px-5 py-4">
          <div className="flex flex-col gap-5">
            {/* ── Resume banner ──────────────────────────────────
                Surfaces ``resumable_job_*`` from the BE sync-status
                probe. When a previous worker run was interrupted, the
                user can either pick up where it left off (incremental)
                or restart from scratch (full re-sync). V1 parity —
                ``EmailSyncProgress.tsx`` block. */}
            {resumableJobId ? (
              <section className="rounded-lg border border-warning/40 bg-warning/[0.06] px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 text-warning" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-[12.5px] text-ink-1">
                      <Trans>Sincronizzazione interrotta</Trans>
                    </p>
                    <p className="text-[11px] text-ink-3">
                      {resumableProcessed > 0
                        ? t`${resumableProcessed.toLocaleString("it-IT")} email già processate.`
                        : t`Una sessione precedente non è stata completata.`}
                    </p>
                  </div>
                </div>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    disabled={forceMut.isPending}
                    onClick={() => void handleResume()}
                    className={cn(
                      "flex-1 rounded-md bg-warning px-2.5 py-1 font-medium text-[11.5px] text-white",
                      "hover:bg-warning/90 disabled:cursor-not-allowed disabled:opacity-50",
                    )}
                  >
                    <Trans>Riprendi</Trans>
                  </button>
                  <button
                    type="button"
                    disabled={forceMut.isPending}
                    onClick={() => void handleForceFull()}
                    className={cn(
                      "flex-1 rounded-md border border-hairline px-2.5 py-1 font-medium text-[11.5px] text-ink-2",
                      "hover:bg-black/[0.04] disabled:cursor-not-allowed disabled:opacity-50",
                    )}
                  >
                    <Trans>Ricomincia da capo</Trans>
                  </button>
                </div>
              </section>
            ) : null}

            {/* ── Presets ─────────────────────────────────────── */}
            {presets.data && presets.data.length > 0 ? (
              <section className="flex flex-col gap-1.5">
                <SectionLabel>
                  <Trans>Preset salvati</Trans>
                </SectionLabel>
                <div className="flex flex-wrap gap-1.5">
                  {presets.data.map((p) => (
                    <div key={p.id} className="group inline-flex items-center">
                      <button
                        type="button"
                        onClick={() => {
                          setFilters(p.filters);
                          toast.info(t`Preset "${p.preset_name}" caricato`);
                        }}
                        className={cn(
                          "inline-flex h-7 items-center gap-1.5 rounded-l-full border border-hairline px-2.5 font-medium text-[11.5px] text-ink-1",
                          "bg-cream/40 hover:bg-cream/70",
                        )}
                      >
                        {p.preset_name}
                      </button>
                      <button
                        type="button"
                        aria-label={t`Elimina preset ${p.preset_name}`}
                        onClick={() => deletePresetMut.mutate(p.id)}
                        className="grid h-7 w-6 place-items-center rounded-r-full border border-hairline border-l-0 bg-cream/40 text-ink-3 hover:bg-rose-50 hover:text-rose-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {/* ── Time range ──────────────────────────────────── */}
            <section className="flex flex-col gap-1.5">
              <SectionLabel>
                <Trans>Intervallo temporale</Trans>
              </SectionLabel>
              <div className="grid grid-cols-2 gap-1.5">
                <RangeChip
                  active={filters.time_range === "7d"}
                  onClick={() => setFilters({ ...filters, time_range: "7d" })}
                >
                  <Trans>Ultimi 7 giorni</Trans>
                </RangeChip>
                <RangeChip
                  active={filters.time_range === "30d"}
                  onClick={() => setFilters({ ...filters, time_range: "30d" })}
                >
                  <Trans>Ultimi 30 giorni</Trans>
                </RangeChip>
                <RangeChip
                  active={filters.time_range === "90d"}
                  onClick={() => setFilters({ ...filters, time_range: "90d" })}
                >
                  <Trans>Ultimi 3 mesi</Trans>
                </RangeChip>
                <RangeChip
                  active={filters.time_range === "custom"}
                  onClick={() => setFilters({ ...filters, time_range: "custom" })}
                >
                  <Trans>Personalizzato</Trans>
                </RangeChip>
              </div>
              {filters.time_range === "custom" ? (
                <div className="mt-1 flex flex-col gap-1.5">
                  <div className="flex gap-1.5">
                    <DateInput label={t`Dal`} value={customAfter} onChange={setCustomAfter} />
                    <DateInput label={t`Al`} value={customBefore} onChange={setCustomBefore} />
                  </div>
                  {customRangeError ? (
                    <p className="text-[11px] text-rose-500">{customRangeError}</p>
                  ) : (
                    <p className="text-[11px] text-ink-3">
                      <Trans>
                        L'intervallo personalizzato può durare fino a {MAX_CUSTOM_RANGE_DAYS}{" "}
                        giorni.
                      </Trans>
                    </p>
                  )}
                </div>
              ) : null}
            </section>

            {/* ── Labels ──────────────────────────────────────── */}
            {labels.data ? (
              <LabelsSection
                labels={labels.data}
                selected={filters.labels ?? []}
                expanded={labelsExpanded}
                onToggleExpanded={() => setLabelsExpanded((s) => !s)}
                onToggle={toggleLabel}
              />
            ) : null}

            {/* ── Quick filters ───────────────────────────────── */}
            <section className="flex flex-col gap-1.5">
              <SectionLabel>
                <Trans>Filtri rapidi</Trans>
              </SectionLabel>
              <div className="grid grid-cols-2 gap-1.5">
                <CheckboxChip
                  checked={!!filters.has_attachment}
                  onChange={(v) => setFilters({ ...filters, has_attachment: v })}
                  icon={<Paperclip className="h-3 w-3" />}
                  label={t`Con allegati`}
                />
                <CheckboxChip
                  checked={!!filters.is_unread}
                  onChange={(v) => setFilters({ ...filters, is_unread: v })}
                  icon={<Mail className="h-3 w-3" />}
                  label={t`Solo non letti`}
                />
              </div>
            </section>

            {/* ── Advanced ────────────────────────────────────── */}
            <section className="flex flex-col gap-1.5">
              <button
                type="button"
                onClick={() => setAdvancedExpanded((s) => !s)}
                className="flex items-center gap-1.5 text-left"
              >
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 text-ink-3 transition-transform",
                    advancedExpanded ? "rotate-0" : "-rotate-90",
                  )}
                />
                <span className="font-semibold text-[10.5px] text-ink-2 uppercase tracking-[0.08em]">
                  <Trans>Avanzate</Trans>
                </span>
              </button>
              {advancedExpanded ? (
                <div className="ml-5 flex flex-col gap-2.5">
                  {provider === "gmail" ? (
                    <div className="flex flex-col gap-1">
                      <label className="text-[11px] text-ink-3">
                        <Trans>Query Gmail</Trans>
                      </label>
                      <input
                        type="text"
                        placeholder="from:boss has:attachment"
                        value={filters.query ?? ""}
                        onChange={(e) => setFilters({ ...filters, query: e.target.value })}
                        className="rounded-md border border-hairline bg-white/55 px-2.5 py-1.5 text-[12px] text-ink-1 placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-ando-orange/45"
                      />
                    </div>
                  ) : null}
                  {/* Max-messages dropdown — V1 parity. The 100k ceiling
                      matches the BE ``EMAIL_MAX_MESSAGES`` default. */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] text-ink-3">
                      <Trans>Numero massimo di email</Trans>
                    </label>
                    <StyledSelect
                      value={maxMessages}
                      onChange={(e) => setMaxMessages(Number(e.target.value))}
                      className="rounded-md border border-hairline bg-white/55 px-2.5 py-1.5 text-[12px] text-ink-1 focus:outline-none focus:ring-2 focus:ring-ando-orange/45"
                    >
                      {MAX_MESSAGES_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n.toLocaleString("it-IT")}
                        </option>
                      ))}
                    </StyledSelect>
                  </div>
                </div>
              ) : null}
            </section>

            {/* ── Preview strip ───────────────────────────────── */}
            <section className="rounded-lg border border-hairline bg-cream/40 px-3 py-2">
              <div className="flex items-center gap-2">
                <Search className="h-3.5 w-3.5 text-ink-3" />
                <p className="flex-1 text-[12px] text-ink-2">
                  {previewing ? (
                    <span className="inline-flex items-center gap-1 text-ink-3">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <Trans>Calcolo…</Trans>
                    </span>
                  ) : matchingCount !== null ? (
                    <>
                      <span className="font-semibold text-ink-1 tabular-nums">
                        {matchingCount.toLocaleString("it-IT")}
                      </span>{" "}
                      <span className="text-ink-3">
                        <Trans>email corrispondenti</Trans>
                        {previewData?.estimated_sync_time_seconds
                          ? ` · ${formatEta(previewData.estimated_sync_time_seconds, i18n)}`
                          : ""}
                      </span>
                    </>
                  ) : (
                    <span className="text-ink-3">
                      <Trans>Anteprima</Trans>
                    </span>
                  )}
                </p>
                <button
                  type="button"
                  aria-label={t`Aggiorna anteprima`}
                  onClick={() =>
                    previewMut.mutate({
                      emailAddress,
                      provider,
                      filters: liveFiltersForSync,
                    })
                  }
                  className="grid h-6 w-6 place-items-center rounded-md text-ink-3 hover:bg-black/[0.04] hover:text-ink-1"
                >
                  <RefreshCw className="h-3 w-3" />
                </button>
              </div>
              {previewData?.gmail_query && provider === "gmail" ? (
                <p className="mt-1 truncate font-mono text-[10.5px] text-ink-3">
                  {previewData.gmail_query}
                </p>
              ) : null}
            </section>
          </div>
        </DrawerBody>

        {/* Footer */}
        <DrawerFooter className="flex flex-col gap-2">
          <div className="flex gap-2">
            <button
              type="button"
              disabled={syncDisabled}
              onClick={handleSync}
              className={cn(
                "inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 font-medium text-[13px]",
                "bg-ando-orange text-white hover:bg-ando-orange/90",
                "disabled:cursor-not-allowed disabled:bg-ink-3/30 disabled:text-ink-3",
              )}
            >
              {triggerMut.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Filter className="h-3.5 w-3.5" />
              )}
              {triggerMut.isPending
                ? t`Sincronizzando…`
                : matchingCount !== null && matchingCount > 0
                  ? t`Sincronizza ${matchingCount.toLocaleString("it-IT")} email`
                  : t`Sincronizza`}
            </button>
            <button
              type="button"
              aria-label={i18n._(msg`Salva come preset`)}
              onClick={() => setSavePresetOpen((s) => !s)}
              disabled={syncDisabled}
              className={cn(
                "grid h-9 w-9 place-items-center rounded-md border border-hairline",
                "text-ink-2 hover:bg-black/[0.04] hover:text-ink-1",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              <Save className="h-3.5 w-3.5" />
            </button>
          </div>
          {savePresetOpen ? (
            <div className="flex gap-2">
              <input
                type="text"
                placeholder={t`Nome del preset`}
                value={savePresetName}
                onChange={(e) => setSavePresetName(e.target.value)}
                className="flex-1 rounded-md border border-hairline bg-white/55 px-2.5 py-1.5 text-[12px] text-ink-1 placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-ando-orange/45"
              />
              <button
                type="button"
                onClick={handleSavePreset}
                disabled={savePresetMut.isPending || savePresetName.trim().length === 0}
                className={cn(
                  "rounded-md bg-ando-orange px-3 py-1.5 font-medium text-[12px] text-white",
                  "hover:bg-ando-orange/90 disabled:cursor-not-allowed disabled:opacity-50",
                )}
              >
                <Trans>Salva</Trans>
              </button>
            </div>
          ) : null}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
EmailSyncFiltersSheet.displayName = "EmailSyncFiltersSheet";

// ──────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="font-semibold text-[10.5px] text-ink-2 uppercase tracking-[0.08em]">
    {children}
  </span>
);

const RangeChip: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      "inline-flex items-center justify-center rounded-md border px-2.5 py-1.5 font-medium text-[12px]",
      active
        ? "border-ando-orange/45 bg-ando-orange-soft text-ando-orange"
        : "border-hairline bg-white/55 text-ink-2 hover:bg-cream/70",
    )}
  >
    {children}
  </button>
);

const DateInput: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
}> = ({ label, value, onChange }) => (
  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
    <label className="text-[10.5px] text-ink-3">{label}</label>
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border border-hairline bg-white/55 px-2 py-1 text-[12px] text-ink-1 focus:outline-none focus:ring-2 focus:ring-ando-orange/45"
    />
  </div>
);

const CheckboxChip: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  icon: React.ReactNode;
  label: string;
}> = ({ checked, onChange, icon, label }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    aria-pressed={checked}
    className={cn(
      "inline-flex items-center justify-center gap-1.5 rounded-md border px-2.5 py-1.5 font-medium text-[12px]",
      checked
        ? "border-ando-orange/45 bg-ando-orange-soft text-ando-orange"
        : "border-hairline bg-white/55 text-ink-2 hover:bg-cream/70",
    )}
  >
    <span className={cn(checked ? "text-ando-orange" : "text-ink-3")}>{icon}</span>
    {label}
  </button>
);

const LabelsSection: React.FC<{
  labels: readonly EmailLabel[];
  selected: readonly string[];
  expanded: boolean;
  onToggleExpanded: () => void;
  onToggle: (id: string) => void;
}> = ({ labels, selected, expanded, onToggleExpanded, onToggle }) => {
  const systemLabels = labels.filter((l) => l.type === "system").slice(0, 6);
  const userLabels = labels.filter((l) => l.type !== "system");
  const selSet = new Set(selected);

  return (
    <section className="flex flex-col gap-1.5">
      <SectionLabel>
        <Trans>Etichette</Trans>
      </SectionLabel>
      <div className="flex flex-wrap gap-1.5">
        {systemLabels.map((l) => (
          <LabelChip
            key={l.id}
            label={l}
            active={selSet.has(l.id)}
            onClick={() => onToggle(l.id)}
          />
        ))}
      </div>
      {userLabels.length > 0 ? (
        <>
          <button
            type="button"
            onClick={onToggleExpanded}
            className="mt-1 inline-flex items-center gap-1 text-left text-[11px] text-ink-3 hover:text-ink-2"
          >
            <ChevronDown
              className={cn("h-3 w-3 transition-transform", expanded ? "rotate-0" : "-rotate-90")}
            />
            <Trans>Etichette personali ({userLabels.length})</Trans>
          </button>
          {expanded ? (
            <div className="flex flex-wrap gap-1.5">
              {userLabels.map((l) => (
                <LabelChip
                  key={l.id}
                  label={l}
                  active={selSet.has(l.id)}
                  onClick={() => onToggle(l.id)}
                />
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
};

const LabelChip: React.FC<{
  label: EmailLabel;
  active: boolean;
  onClick: () => void;
}> = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]",
      active
        ? "border-ando-orange/45 bg-ando-orange-soft text-ando-orange"
        : "border-hairline bg-white/55 text-ink-2 hover:bg-cream/70",
    )}
  >
    <span className="max-w-[140px] truncate">{label.name}</span>
    {typeof label.message_count === "number" ? (
      <span className="text-[10px] text-ink-3 tabular-nums">
        {label.message_count > 999
          ? `${(label.message_count / 1000).toFixed(0)}k`
          : label.message_count}
      </span>
    ) : null}
  </button>
);
